import type {
  CreateInvoiceRequest,
  InvoiceResponse,
  UpdateInvoiceRequest,
} from '@palaccount/contracts';
import { PICKER_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { api } from '@/api';
import type { TypeaheadOption } from '@/components/ui/typeahead';
import { PARTNERS_KEY } from '@/features/partners/api';
import { throwApiError } from '@/lib/server-messages';
import { useDebounced, usePickerState, type PickerState } from '@/lib/typeahead';

/** SAL-04 data layer — same shape as the partners one. */

export const INVOICES_KEY = ['invoices'] as const;

export interface InvoiceListFilters {
  partnerId?: string | undefined;
  status?: InvoiceResponse['status'] | undefined;
  /** The search box: printed number, customer, and G-11's per-customer reference. */
  q?: string | undefined;
  /** Wave 3B — the list's window on the invoice date, both inclusive. */
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

function invoiceQuery(filters: InvoiceListFilters) {
  return {
    ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  };
}

/** SAL-04's list, one page at a time (NFR-42): 2.95 s on 12,000 invoices. */
export function useInvoices(filters: InvoiceListFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...INVOICES_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.invoices.list({ query: { ...invoiceQuery(filters), page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** The export's page read — the same query the screen shows, imperative so `fetchAllPages` can walk it. */
export async function fetchInvoicesPage(
  filters: InvoiceListFilters,
  page: number,
  pageSize: number,
): Promise<{ rows: InvoiceResponse[]; total: number }> {
  const response = await api.invoices.list({ query: { ...invoiceQuery(filters), page, pageSize } });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return { rows: response.body.invoices, total: response.body.total };
}

/** Wave 3B — SAL-04's list tiles over the whole register (PAGINATION.md §3 step 10). */
export function useInvoiceStats() {
  return useQuery({
    queryKey: [...INVOICES_KEY, 'stats'],
    queryFn: async () => {
      const response = await api.invoices.stats();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useInvoice(invoiceId: string | undefined) {
  return useQuery({
    queryKey: [...INVOICES_KEY, 'one', invoiceId],
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceResponse> => {
      const response = await api.invoices.get({ params: { invoiceId: invoiceId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * GEN-50 — one PANEL of invoices for what is being typed (number, customer,
 * reference — the list route's own search), never the whole list. Off
 * while `enabled` is false: the stand-alone return's picker has no customer
 * to narrow by until one is chosen, and a panel of every posted invoice in
 * the company is not an answer to anything.
 */
function useInvoiceSearch(q: string, filters: InvoiceListFilters, enabled: boolean) {
  return useQuery({
    queryKey: [...INVOICES_KEY, 'search', q, filters],
    enabled,
    queryFn: async (): Promise<InvoiceResponse[]> => {
      const response = await api.invoices.list({
        query: {
          ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(q ? { q } : {}),
          page: 1,
          pageSize: PICKER_PAGE_SIZE,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.invoices;
    },
    placeholderData: keepPreviousData,
  });
}

export function invoiceOption(invoice: InvoiceResponse): TypeaheadOption {
  return { id: invoice.id, label: `${invoice.code} — ${invoice.partnerName}` };
}

/**
 * The invoice typeahead's state, the shape `useBillPicker` gives the
 * purchase return (wave 1B): matches for the typed text under `filters`
 * (the stand-alone sales return passes the chosen customer and
 * `status: 'posted'` — BR-SAL-05-02, a return is built from a POSTED
 * invoice), plus the one record the form already holds, resolved by id so a
 * saved pick renders even when the search would not find it.
 */
export function useInvoicePicker(
  heldId: string | null | undefined,
  filters: InvoiceListFilters = {},
  enabled = true,
): PickerState<InvoiceResponse> {
  const [query, setQuery] = React.useState('');
  const search = useDebounced(query);
  const matches = useInvoiceSearch(search, filters, enabled);
  const held = useInvoice(heldId ?? undefined);
  const heldRows = React.useMemo(() => (held.data ? [held.data] : undefined), [held.data]);
  return usePickerState<InvoiceResponse>({
    query,
    setQuery,
    search,
    matches: matches.data,
    held: heldRows,
    toOption: invoiceOption,
    loading: matches.isFetching,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateInvoiceRequest): Promise<InvoiceResponse> => {
      const response = await api.invoices.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVOICES_KEY }),
  });
}

export function useUpdateInvoice(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateInvoiceRequest): Promise<InvoiceResponse> => {
      const response = await api.invoices.update({ params: { invoiceId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVOICES_KEY }),
  });
}

export function useDeleteInvoice(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.invoices.remove({ params: { invoiceId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INVOICES_KEY }),
  });
}

/** Draft → Posted; the customer's balance moves, so partners refresh too. */
export function usePostInvoice(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<InvoiceResponse> => {
      const response = await api.invoices.post({ params: { invoiceId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });
}

/**
 * Posted → Cancelled (ruling A6). The customer's balance moves back, so
 * partners refresh for the same reason posting refreshes them.
 *
 * GEN-59: the cancel carries its typed reason, and the `mutationFn` takes it
 * as its sole argument — the shape every reason-carrying mutation in this
 * build has, so the screen calls `mutateAsync(reason)` and the string reaches
 * the body untransformed.
 */
export function useCancelInvoice(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<InvoiceResponse> => {
      const response = await api.invoices.cancel({ params: { invoiceId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: PARTNERS_KEY });
    },
  });
}
